<template>
    <div>
        <div class="l-padded u-bb">
            <SearchField v-model="search" />

            <div
                class="l-inline l-gap-1 t-small adjusting-mode-switch"
                @click="switchAdjustingMode"
            >
                <input
                    type="checkbox"
                    :checked="shouldAdjustMapOnFilterChange"
                />

                <p>{{ $t('adjustMapOnFilterChange') }}</p>
            </div>

            <div
                v-if="all"
                class="l-inline l-gap-1 t-small adjusting-mode-switch"
                @click="setAssetFilterActive(!isAssetFilterActive)"
            >
                <input type="checkbox" :checked="isAssetFilterActive" />

                <p>{{ $t('keepFilterParams') }}</p>
            </div>

            <slot name="checkboxes" />
        </div>

        <ListHeader class="u-bb">
            <div slot="title">
                <span v-if="listHeaderState == listHeaderConsts.none">
                    {{ $t('listedOf', [filteredAssets.length, assets.length]) }}
                </span>

                <span v-if="listHeaderState == listHeaderConsts.sort">
                    {{ $t('sortedBy') }}
                </span>

                <span v-if="listHeaderState == listHeaderConsts.filter">
                    {{ $t('filterBy') }}
                </span>
            </div>

            <div slot="accessory" class="l-inline l-gap-2">
                <IconButton
                    v-if="listHeaderState == listHeaderConsts.none"
                    @click="listHeaderState = listHeaderConsts.sort"
                >
                    <SortIcon color="black" />
                </IconButton>

                <IconButton
                    v-if="listHeaderState == listHeaderConsts.none"
                    @click="listHeaderState = listHeaderConsts.filter"
                >
                    <MarkedIcon :marked="isFiltered">
                        <FilterIcon />
                    </MarkedIcon>
                </IconButton>

                <IconButton
                    v-if="listHeaderState != listHeaderConsts.none"
                    @click="listHeaderState = listHeaderConsts.none"
                >
                    <RemoveIcon />
                </IconButton>
            </div>

            <div slot="content">
                <transition name="dropdown">
                    <div
                        v-if="listHeaderState == listHeaderConsts.sort"
                        class="sort-panel l-stack l-padded l-gap-1 dropdown"
                    >
                        <div
                            v-for="(item, index, key) in sortData"
                            :key="key"
                            class="l-inline l-center-v l-gap-2 clickable"
                            @click="
                                assetListFilterPreferences.sortBy = index + 1
                            "
                        >
                            <input
                                type="radio"
                                :checked="
                                    assetListFilterPreferences.sortBy ==
                                        index + 1
                                "
                            />
                            <div>{{ item.text }}</div>
                        </div>
                    </div>

                    <div
                        v-if="listHeaderState == listHeaderConsts.filter"
                        class="sort-panel l-stack l-padded l-gap-1 dropdown"
                    >
                        <div
                            class="l-inline l-gap-2 l-center-v clickable"
                            @click="toggleFilterPreference('filterByInactive')"
                        >
                            <input
                                type="checkbox"
                                :checked="
                                    assetListFilterPreferences.filterByInactive
                                "
                            />

                            <DisabledIcon />

                            <p>{{ $t('inactive') }}</p>
                        </div>

                        <div
                            class="l-inline l-gap-2 l-center-v clickable"
                            @click="toggleFilterPreference('filterByAlert')"
                        >
                            <input
                                type="checkbox"
                                :checked="
                                    assetListFilterPreferences.filterByAlert
                                "
                            />

                            <WarningIcon />

                            <p>{{ $t('hasAlert') }}</p>
                        </div>

                        <div
                            class="l-inline l-gap-2 l-center-v clickable"
                            @click="
                                toggleFilterPreference('filterByRecentlyActive')
                            "
                        >
                            <input
                                type="checkbox"
                                :checked="
                                    assetListFilterPreferences.filterByRecentlyActive
                                "
                            />

                            <RecentIcon />

                            <p>{{ $t('recentlyActive') }}</p>
                        </div>

                        <div class="offset">
                            <BaseMultiselect
                                v-show="
                                    assetListFilterPreferences.filterByRecentlyActive
                                "
                                :value="lastActiveFor"
                                block
                                :options="lastActiveOptions"
                                track-by="duration"
                                :custom-label="option => $t(option.duration)"
                                :searchable="false"
                                @input="handleLastActiveForChange"
                            />
                        </div>

                        <div class="split">
                            <div class="u-bb" />
                        </div>

                        <AssetTypeSelect
                            :value="assetListFilterPreferences.filterByType"
                            :placeholder="$t('type')"
                            :multiple="true"
                            @input="handleTypeChange"
                        />

                        <LocationSelect
                            :value="assetListFilterPreferences.filterByLocation"
                            :placeholder="$t('location')"
                            :multiple="true"
                            @input="handleLocationChange"
                        />

                        <ColorInput
                            :value="assetListFilterPreferences.filterByColor"
                            @input="handleColorChange"
                        />
                    </div>
                </transition>
            </div>
        </ListHeader>

        <List
            v-infinite-scroll="loadMoreAssets"
            :infinite-scroll-distance="scrollStep"
        >
            <AssetListItem
                v-for="asset in assetsPortion"
                :key="`AssetListItem${asset.id}`"
                :asset="asset"
                :asset-detail-link-base="assetDetailLinkBase"
            />
        </List>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { debounce, isEmpty } from 'lodash'
import infiniteScroll from 'vue-infinite-scroll'

import AssetListItem from './AssetListItem'
import AssetTypeSelect from './AssetTypeSelect'
import BaseMultiselect from './redesigned/BaseMultiselect'
import ColorInput from './ColorInput'
import DisabledIcon from './icons/DisabledIcon'
import FilterIcon from './icons/FilterIcon'
import IconButton from './IconButton'
import List from './List'
import ListHeader from './ListHeader'
import LocationSelect from './LocationSelect'
import MarkedIcon from './MarkedIcon'
import RecentIcon from './icons/RecentIcon'
import RemoveIcon from './icons/RemoveIcon'
import SearchField from './SearchField'
import SortIcon from './icons/SortIcon'
import WarningIcon from './icons/WarningIcon'
import { trackerHelper } from '@/utils'

const sortConst = {
    NameAcs: 1,
    NameDesc: 2,
    BatteryStatusAsc: 3,
    BatteryStatusDesc: 4,
    LastGPSAsc: 5,
    LastGPSDesc: 6,
}
const defaultSort = sortConst.NameAcs
const defaultScrollLimit = 15
// conservative estimate to preload items for scroll position restoration
const itemHeight = 70

export default {
    name: 'AssetList',
    components: {
        AssetListItem,
        AssetTypeSelect,
        BaseMultiselect,
        ColorInput,
        DisabledIcon,
        FilterIcon,
        IconButton,
        List,
        ListHeader,
        LocationSelect,
        MarkedIcon,
        RecentIcon,
        RemoveIcon,
        SearchField,
        SortIcon,
        WarningIcon,
    },
    directives: {
        infiniteScroll,
    },
    props: {
        all: {
            type: Boolean,
            default: false,
        },
        assetDetailLinkBase: {
            type: String,
            default: '',
        },
        assets: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            lastActiveOptions: [
                { duration: '1h', milliseconds: 3600000 },
                { duration: '6h', milliseconds: 21600000 },
                { duration: '12h', milliseconds: 43200000 },
                { duration: '1d', milliseconds: 86400000 },
                { duration: '1w', milliseconds: 604800000 },
                { duration: '1m', milliseconds: 2629746000 },
            ],
            listHeaderConsts: {
                none: 0,
                filter: 1,
                sort: 2,
            },
            listHeaderState: 0,
            scrollLimit: defaultScrollLimit,
            search: '',
            sortData: [
                { text: this.$t('nameAZ'), value: sortConst.NameAcs },
                { text: this.$t('nameZA'), value: sortConst.NameDesc },
                {
                    text: this.$t('batteryEmptyFull'),
                    value: sortConst.BatteryStatusAsc,
                },
                {
                    text: this.$t('batteryFullEmpty'),
                    value: sortConst.BatteryStatusDesc,
                },
                {
                    text: this.$t('lastContactNewest'),
                    value: sortConst.LastMessageAsc,
                },
                {
                    text: this.$t('lastContactOldest'),
                    value: sortConst.LastMessageDesc,
                },
            ],
            shouldKeepFilterParams: false,
        }
    },
    computed: {
        ...mapState('tracker', ['shouldAdjustMapOnFilterChange']),
        ...mapState('common', ['sidePanelScrollPosition']),
        ...mapState('preferences', ['isAssetFilterActive']),
        ...mapGetters('preferences', ['assetListFilterPreferences']),
        isFiltered() {
            return (
                this.assetListFilterPreferences.filterByInactive ||
                this.assetListFilterPreferences.filterByAlert ||
                this.assetListFilterPreferences.filterByRecentlyActive ||
                !!this.assetListFilterPreferences.filterByType.length ||
                !!this.assetListFilterPreferences.filterByLocation.length ||
                !!this.assetListFilterPreferences.filterByColor.length
            )
        },
        filteredAssets() {
            let result = trackerHelper.filterTrackers(
                this.assets,
                this.assetListFilterPreferences
            )

            //Sort with sort box
            const sortBy = this.assetListFilterPreferences.sortBy
            if (sortBy != '') {
                switch (sortBy) {
                    case sortConst.NameAcs: {
                        result = result.slice().sort((a, b) => {
                            return a.asset_details.name.localeCompare(
                                b.asset_details.name,
                                undefined,
                                { numeric: true }
                            )
                        })
                        break
                    }
                    case sortConst.NameDesc: {
                        result = result.slice().sort((a, b) => {
                            return b.asset_details.name.localeCompare(
                                a.asset_details.name,
                                undefined,
                                { numeric: true }
                            )
                        })
                        break
                    }
                    case sortConst.BatteryStatusAsc: {
                        result = result.slice().sort((a, b) => {
                            const valueA =
                                a.asset_details.sensor_data?.battery?.value
                            const valueB =
                                b.asset_details.sensor_data?.battery?.value
                            if (valueA == null || valueA > valueB) return 1
                            if (valueB == null || valueA < valueB) return -1
                            return 0
                        })
                        break
                    }
                    case sortConst.BatteryStatusDesc: {
                        result = result.slice().sort((a, b) => {
                            const valueA =
                                a.asset_details.sensor_data?.battery?.value
                            const valueB =
                                b.asset_details.sensor_data?.battery?.value
                            if (valueA == null || valueA < valueB) return 1
                            if (valueB == null || valueA > valueB) return -1
                            return 0
                        })
                        break
                    }
                    case sortConst.LastGPSAsc: {
                        result = result.slice().sort((a, b) => {
                            const timeA = a.last_message_timestamp
                            const timeB = b.last_message_timestamp
                            if (!timeA || timeA < timeB) return 1
                            if (!timeB || timeA > timeB) return -1
                            return 0
                        })
                        break
                    }
                    case sortConst.LastGPSDesc: {
                        result = result.slice().sort((a, b) => {
                            const timeA = a.last_message_timestamp
                            const timeB = b.last_message_timestamp
                            if (!timeA || timeA > timeB) return 1
                            if (!timeB || timeA < timeB) return -1
                            return 0
                        })
                        break
                    }
                }
            }
            return result
        },
        assetsPortion() {
            return this.filteredAssets.length === this.assets.length
                ? this.filteredAssets.slice(0, this.scrollLimit)
                : this.filteredAssets
        },
        scrollStep() {
            const minItems = window.document.body.clientHeight / itemHeight
            return Math.max(defaultScrollLimit, minItems)
        },
        lastActiveFor() {
            return (
                this.lastActiveOptions.find(
                    option =>
                        option.milliseconds ===
                        this.assetListFilterPreferences.lastActiveFor
                ) || this.lastActiveOptions[1]
            )
        },
    },
    watch: {
        $route(to) {
            this.setFromRouteQuery(to.query)
        },
        assets(newAssets, oldAssets) {
            if (
                newAssets.length !== oldAssets.length ||
                newAssets.some(
                    (asset, index) => asset.id !== oldAssets[index].id
                ) // assumes asset list sorted by id
            ) {
                this.resetFilter()
            }
        },
        assetListFilterPreferences: {
            deep: true,
            immediate: true,
            handler: function() {
                this.updateRoute()
            },
        },
        filteredAssets: {
            deep: true,
            immediate: true,
            handler() {
                this.setFilteredTrackers(this.filteredAssets)
            },
        },
        search() {
            this.updatePreferences({
                assetListFilterPreferences: {
                    search: this.search,
                },
            })
        },
    },
    mounted() {
        if (!isEmpty(this.$route.query)) {
            this.setFromRouteQuery(this.$route.query)
        }
        if (this.sidePanelScrollPosition[this.$route.fullPath]) {
            this.scrollLimit =
                Math.ceil(
                    this.sidePanelScrollPosition[this.$route.fullPath] /
                        itemHeight
                ) + defaultScrollLimit
        }
        if (this.assetListFilterPreferences.search) {
            this.search = this.assetListFilterPreferences.search
        }
    },
    beforeDestroy() {
        this.resetFilteredTrackers()
    },
    methods: {
        ...mapMutations('tracker', [
            'setFilteredTrackers',
            'setShouldAdjustMapOnFilterChange',
        ]),
        ...mapActions('preferences', ['updatePreferences']),
        ...mapActions('tracker', ['resetFilteredTrackers']),
        setFromRouteQuery(query) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    filterByColor: query.color ? [].concat(query.color) : [],
                    filterByInactive: !!query.inactive,
                    filterByAlert: !!query.alert,
                    filterByRecentlyActive: !!query.recent,
                    filterByLocation: query.location
                        ? [].concat(query.location).map(item => +item)
                        : [],
                    filterByType: query.type
                        ? [].concat(query.type).map(item => +item)
                        : [],
                    sortBy: +query.sort || defaultSort,
                    search: query.search || '',
                },
            })
        },
        updateRoute: debounce(function() {
            let query = {}
            const {
                search,
                filterByInactive,
                filterByAlert,
                filterByRecentlyActive,
                filterByType,
                filterByLocation,
                filterByColor,
                sortBy,
            } = this.assetListFilterPreferences

            if (filterByColor.length) query.color = filterByColor
            if (filterByInactive) query.inactive = filterByInactive
            if (filterByAlert) query.alert = filterByAlert
            if (filterByRecentlyActive) query.recent = filterByRecentlyActive
            if (filterByLocation.length) query.location = filterByLocation
            if (filterByType.length) query.type = filterByType
            if (search) query.search = search
            if (sortBy) query.sort = sortBy

            this.$router.replace({ params: this.$route.params, query })
        }, 500),
        resetFilter() {
            this.updatePreferences({
                assetListFilterPreferences: {
                    filterByColor: [],
                    filterByInactive: false,
                    filterByAlert: false,
                    filterByRecentlyActive: false,
                    filterByLocation: [],
                    filterByType: [],
                    lastActiveFor: 21600000,
                    sortBy: defaultSort,
                    search: '',
                },
            })
        },
        loadMoreAssets() {
            if (this.scrollLimit < this.assets.length) {
                this.scrollLimit += this.scrollStep
            }
        },
        switchAdjustingMode() {
            this.setShouldAdjustMapOnFilterChange(
                !this.shouldAdjustMapOnFilterChange
            )
        },
        setAssetFilterActive(isAssetFilterActive) {
            this.updatePreferences({ isAssetFilterActive })
        },
        toggleFilterPreference(key) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    [key]: !this.assetListFilterPreferences[key],
                },
            })
        },
        handleLastActiveForChange(lastActiveFor) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    lastActiveFor: lastActiveFor.milliseconds || 21600000,
                },
            })
        },
        handleTypeChange(assetTypes) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    filterByType: assetTypes || [],
                },
            })
        },
        handleLocationChange(locations) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    filterByLocation: locations || [],
                },
            })
        },
        handleColorChange(colors) {
            this.updatePreferences({
                assetListFilterPreferences: {
                    filterByColor: colors || [],
                },
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "12h": "in the last 12 hours",
        "1d": "in the last day",
        "1h": "in the last hour",
        "1m": "in the last month",
        "1w": "in the last week",
        "6h": "in the last 6 hours",
        "adjustMapOnFilterChange": "Center map on filter results",
        "batteryEmptyFull": "Battery level (empty → full)",
        "batteryFullEmpty": "Battery level (full → empty)",
        "filterBy": "Filter",
        "hasAlert": "Has alert",
        "inactive": "Inactive",
        "keepFilterParams": "Keep filter parameters",
        "lastContactNewest": "Last contact (recent → old)",
        "lastContactOldest": "Last contact (old → recent)",
        "listedOf": "Show {0} of {1}",
        "location": "Location",
        "lowBattery": "Low battery",
        "nameAZ": "Name (A → Z)",
        "nameZA": "Name (Z → A)",
        "recentlyActive": "Active",
        "sortedBy": "Sorted by ",
        "type": "Type"
    },
    "de": {
        "12h": "in den letzten 12 Stunden",
        "1d": "im vergangenen Tag",
        "1h": "in der vergangenen Stunde",
        "1m": "im vergangenen Monat",
        "1w": "in der vergangenen Woche",
        "6h": "in den letzten 6 Stunden",
        "adjustMapOnFilterChange": "Karte auf die Filterergebnisse zentrieren",
        "batteryEmptyFull": "Batteriestatus (Leer → Voll)",
        "batteryFullEmpty": "Batteriestatus (Voll → Leer)",
        "filterBy": "Filter",
        "hasAlert": "Hat Alarm",
        "inactive": "Inaktiv",
        "keepFilterParams": "Filter-Parameter beibehalten",
        "lastContactNewest": "Letzter Kontakt (Neu → Alt)",
        "lastContactOldest": "Letzter Kontakt (Alt → Neu)",
        "listedOf": "Zeige {0} von {1}",
        "location": "Standort",
        "lowBattery": "Niedriger Batteriestand",
        "nameAZ": "Name (A → Z)",
        "nameZA": "Name (Z → A)",
        "recentlyActive": "Aktiv",
        "sortedBy": "Sortiert nach ",
        "type": "Typ"
    },
    "fr": {
        "12h": "Au cours des 12 dernières heures",
        "1d": "Au cours du dernier jour",
        "1h": "Au cours de la dernière heure",
        "1m": "Au cours du dernier mois",
        "1w": "Au cours de la dernière semaine",
        "6h": "Au cours des 6 dernières heures",
        "adjustMapOnFilterChange": "Centrer la carte sur les résultats du filtre",
        "batteryEmptyFull": "Niveau de la batterie (vide → plein)",
        "batteryFullEmpty": "Niveau de la batterie (plein → vide)",
        "filterBy": "Filtre",
        "hasAlert": "A alerté",
        "inactive": "Inactif",
        "keepFilterParams": "Conserver les paramètres du filtre",
        "lastContactNewest": "Dernier contact (récent → ancien)",
        "lastContactOldest": "Dernier contact (ancien → récent)",
        "listedOf": "Montrer {0} de {1}",
        "location": "Emplacement",
        "lowBattery": "Batterie faible",
        "nameAZ": "Nom (A → Z)",
        "nameZA": "Nom (Z → A)",
        "recentlyActive": "Actif",
        "sortedBy": "Trié par",
        "type": "Type"
    },
    "it": {
        "12h": "nelle ultime 12 ore",
        "1d": "nell'ultimo giorno",
        "1h": "nell'ultima ora",
        "1m": "nell'ultimo mese",
        "1w": "nell'ultima settimana",
        "6h": "nelle ultime 6 ore",
        "adjustMapOnFilterChange": "Centrare la mappa sui risultati del filtro",
        "batteryEmptyFull": "Livello Batteria (Vuota → Piena)",
        "batteryFullEmpty": "Livello Batteria (Piena → Vuota)",
        "filterBy": "Filtro",
        "hasAlert": "Cappello Allarme",
        "inactive": "Inattivo",
        "keepFilterParams": "Mantenere i parametri del filtro",
        "lastContactNewest": "Ultima Contatto (Recente → Vecchia)",
        "lastContactOldest": "Ultima Contatto (Vecchia → Recente)",
        "listedOf": "Listo {0} di {1}",
        "location": "Locazione",
        "lowBattery": "Poca Batteria",
        "nameAZ": "Nome (A → Z)",
        "nameZA": "Nome (Z → A)",
        "recentlyActive": "Attivo",
        "sortedBy": "Diviso tra ",
        "type": "Typo"
    }
}
</i18n>

<style lang="scss" scoped>
.adjusting-mode-switch {
    margin-top: 1rem;
    margin-left: 0.5rem;
    cursor: pointer;
}

.sort-panel {
    background: $color-gray-lighter;

    .offset {
        margin-left: 20px;
    }

    .split {
        padding: 0.5rem 0;
    }
}

.select-li {
    color: darkgray;
}

.dropdown {
    &-enter,
    &-leave-to {
        max-height: 0;
        opacity: 0;
    }

    &-leave,
    &-enter-to {
        max-height: 400px;
        opacity: 1;
    }

    &-enter-active,
    &-leave-active {
        transition: max-height 0.2s linear, opacity 0.4s ease-in-out;
    }
}
</style>
